import React from 'react';
import { motion } from 'framer-motion';
import { LANDING_COLORS } from './LandingConstants';

/**
 * FulguratingLogo - Animated logo with "fulgurating" (dazzling/blazing) effect
 * Features rotating rings, orbiting dots, pulsing glow, and energy sparks
 */
export default function FulguratingLogo() {
  const logoUrl = "https://customer-assets.emergentagent.com/job_1746db74-4706-48f0-ad11-2846005fb802/artifacts/fsttjs0j_Motivya%20logo.png";
  
  return (
    <motion.div
      initial={{ scale: 0.8, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ type: "spring", duration: 1, bounce: 0.3 }}
      className="relative flex items-center justify-center"
      style={{ width: '350px', height: '350px' }}
    >
      {/* Rotating outer ring */}
      <motion.div
        className="absolute w-full h-full rounded-full"
        style={{ 
          border: `3px solid transparent`,
          borderTopColor: LANDING_COLORS.primary,
          borderRightColor: '#22c55e',
        }}
        animate={{ rotate: 360 }}
        transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
      />
      
      {/* Counter-rotating inner ring */}
      <motion.div
        className="absolute rounded-full"
        style={{ 
          width: '90%',
          height: '90%',
          border: `2px solid transparent`,
          borderBottomColor: LANDING_COLORS.primary,
          borderLeftColor: '#3b82f6',
        }}
        animate={{ rotate: -360 }}
        transition={{ duration: 4, repeat: Infinity, ease: 'linear' }}
      />
      
      {/* Pulsing glow */}
      <motion.div
        className="absolute rounded-full blur-2xl"
        style={{ 
          width: '70%',
          height: '70%',
          background: `radial-gradient(circle, ${LANDING_COLORS.primary}50, transparent 70%)`,
        }}
        animate={{ 
          scale: [1, 1.3, 1],
          opacity: [0.5, 0.8, 0.5],
        }}
        transition={{ duration: 2, repeat: Infinity }}
      />
      
      {/* Orbiting dots */}
      {[0, 1, 2, 3].map((i) => (
        <motion.div
          key={i}
          className="absolute"
          style={{
            width: '100%',
            height: '100%',
          }}
          animate={{ rotate: 360 }}
          transition={{ 
            duration: 2 + i * 0.5, 
            repeat: Infinity, 
            ease: 'linear',
            delay: i * 0.3,
          }}
        >
          <div 
            className="absolute w-3 h-3 rounded-full"
            style={{ 
              background: i % 2 === 0 ? LANDING_COLORS.primary : '#22c55e',
              top: '0%',
              left: '50%',
              transform: 'translateX(-50%)',
              boxShadow: `0 0 10px ${i % 2 === 0 ? LANDING_COLORS.primary : '#22c55e'}`,
            }}
          />
        </motion.div>
      ))}
      
      {/* Logo with glow effect */}
      <motion.div
        className="relative z-10"
        animate={{ 
          scale: [1, 1.05, 1],
        }}
        transition={{ duration: 2, repeat: Infinity }}
      >
        <motion.div
          animate={{ 
            filter: [
              'drop-shadow(0 0 20px rgba(215, 222, 59, 0.6))',
              'drop-shadow(0 0 40px rgba(215, 222, 59, 0.9))',
              'drop-shadow(0 0 20px rgba(215, 222, 59, 0.6))',
            ]
          }}
          transition={{ duration: 1.5, repeat: Infinity }}
        >
          <img
            src={logoUrl}
            alt="Motivya Logo"
            className="w-48 h-48 md:w-56 md:h-56 object-contain"
          />
        </motion.div>
      </motion.div>
      
      {/* Energy sparks */}
      {[...Array(6)].map((_, i) => (
        <motion.div
          key={`spark-${i}`}
          className="absolute w-1 h-1 rounded-full"
          style={{
            background: LANDING_COLORS.primary,
            boxShadow: `0 0 6px ${LANDING_COLORS.primary}`,
          }}
          animate={{
            x: [0, Math.cos(i * 60 * Math.PI / 180) * 150, 0],
            y: [0, Math.sin(i * 60 * Math.PI / 180) * 150, 0],
            opacity: [0, 1, 0],
            scale: [0.5, 1.5, 0.5],
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            delay: i * 0.3,
          }}
        />
      ))}
    </motion.div>
  );
}
